// Update SiteContext to use new token getter name
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { defaultSiteData, loadSiteData, saveSiteData } from '../mock';
import { fetchSiteConfig, updateSiteConfig, resetSiteConfigRemote, getAdminToken } from '../lib/api';

const SiteContext = createContext(null);

// Wallet/deposit flags are cached in localStorage so a page refresh doesn't
// briefly flash the "enabled" UI when the admin has actually disabled them.
const FLAGS_KEY = 'site_flags_v1';
function readCachedFlags() {
  try {
    const f = JSON.parse(localStorage.getItem(FLAGS_KEY) || 'null');
    if (f && typeof f === 'object') return {
      wallet_user_enabled: f.wallet_user_enabled !== false,
      wallet_admin_enabled: f.wallet_admin_enabled !== false,
      deposit_enabled: f.deposit_enabled !== false,
    };
  } catch { /* */ }
  return { wallet_user_enabled: true, wallet_admin_enabled: true, deposit_enabled: true };
}
function writeCachedFlags(f) {
  try { localStorage.setItem(FLAGS_KEY, JSON.stringify(f)); } catch { /* */ }
}

export function SiteProvider({ children }) {
  const [data, setData] = useState(loadSiteData());
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const cached = readCachedFlags();
  const [walletUserEnabled, setWalletUserEnabled] = useState(cached.wallet_user_enabled);
  const [walletAdminEnabled, setWalletAdminEnabled] = useState(cached.wallet_admin_enabled);
  const [depositEnabled, setDepositEnabled] = useState(cached.deposit_enabled);

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // Single shared loader — used at mount, on poll, and after admin toggles
  // so updates propagate instantly across the whole app (no page refresh).
  const refresh = useCallback(async () => {
    try {
      const { data: cfg, tenant: t, wallet_user_enabled, wallet_admin_enabled, deposit_enabled } = await fetchSiteConfig();
      if (!aliveRef.current) return;
      const flags = {
        wallet_user_enabled: wallet_user_enabled !== false,
        wallet_admin_enabled: wallet_admin_enabled !== false,
        deposit_enabled: deposit_enabled !== false,
      };
      setData({ ...defaultSiteData, ...cfg });
      setTenant(t || null);
      setWalletUserEnabled(flags.wallet_user_enabled);
      setWalletAdminEnabled(flags.wallet_admin_enabled);
      setDepositEnabled(flags.deposit_enabled);
      writeCachedFlags(flags);
      saveSiteData(cfg);
    } catch { /* keep cache */ }
  }, []);

  // Initial load once on mount. Updates propagate via storage and explicit events, preventing heavy polling load.
  useEffect(() => {
    refresh().finally(() => setLoading(false));

    const onVisibility = () => {
      if (!document.hidden) refresh();
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Listen for cross-tab updates (admin tab → user tab)
    const onStorage = (e) => { if (e.key === FLAGS_KEY) refresh(); };
    window.addEventListener('storage', onStorage);

    // Listen for explicit in-tab update event
    const onUpd = () => refresh();
    window.addEventListener('site-flags-updated', onUpd);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('site-flags-updated', onUpd);
    };
  }, [refresh]);

  // Apply favicon + page title dynamically whenever branding changes
  useEffect(() => {
    const fav = data?.branding?.favicon;
    const shape = data?.branding?.faviconShape || 'square';
    if (data?.branding?.logoText) document.title = `${data.branding.logoText} ${data.branding.logoAccent || ''}`.trim();
    let link = document.querySelector("link[rel~='icon']");
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    if (!fav) { link.href = '/favicon.ico'; return; }
    if (shape === 'square') { link.href = fav; return; }
    try {
      const img = new Image(); img.crossOrigin = 'anonymous';
      img.onload = () => {
        const size = 128;
        const c = document.createElement('canvas');
        c.width = size; c.height = size;
        const ctx = c.getContext('2d');
        ctx.save();
        if (shape === 'circle') { ctx.beginPath(); ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2); ctx.closePath(); ctx.clip(); }
        else if (shape === 'rounded') { const r = 18; ctx.beginPath(); ctx.moveTo(r, 0); ctx.lineTo(size - r, 0); ctx.quadraticCurveTo(size, 0, size, r); ctx.lineTo(size, size - r); ctx.quadraticCurveTo(size, size, size - r, size); ctx.lineTo(r, size); ctx.quadraticCurveTo(0, size, 0, size - r); ctx.lineTo(0, r); ctx.quadraticCurveTo(0, 0, r, 0); ctx.closePath(); ctx.clip(); }
        ctx.drawImage(img, 0, 0, size, size);
        ctx.restore();
        link.href = c.toDataURL('image/png');
      };
      img.src = fav;
    } catch { link.href = fav; }
  }, [data?.branding?.favicon, data?.branding?.faviconShape, data?.branding?.logoText, data?.branding?.logoAccent]);

  // Dynamically set CSS variables (--gold, --gold-dark, --active-green, --page-bg)
  // across the document whenever branding colors change.
  useEffect(() => {
    const root = document.documentElement;
    if (!root) return;
    const b = data?.branding || {};
    if (b.accent) root.style.setProperty('--gold', b.accent);
    if (b.accentDark) root.style.setProperty('--gold-dark', b.accentDark);
    if (b.activeGreen) root.style.setProperty('--active-green', b.activeGreen);
    if (b.pageBg) {
      root.style.setProperty('--page-bg', b.pageBg);
      if (document.body) document.body.style.backgroundColor = b.pageBg;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.branding?.accent, data?.branding?.accentDark, data?.branding?.activeGreen, data?.branding?.pageBg]);

  const update = (nextOrFn) => {
    // Admin sections call this with an updater function:
    //   update((prev) => ({ ...prev, products }))
    // Previously the function was passed straight to saveSiteData() and
    // updateSiteConfig(), which serialised the function (not the resolved
    // object) — the backend silently saved nothing, so newly uploaded videos
    // / new rates / new links never persisted. Resolve the updater INSIDE
    // setData's callback so we always have the fully-merged next-state object
    // and forward THAT to both localStorage and the backend.
    setData((curr) => {
      const next = typeof nextOrFn === 'function' ? nextOrFn(curr) : nextOrFn;
      saveSiteData(next);
      if (getAdminToken()) updateSiteConfig(next).catch(() => {});
      return next;
    });
  };

  const updateSection = (key, value) => {
    const next = { ...data, [key]: value };
    update(next);
  };

  const reset = async () => {
    if (getAdminToken()) { try { await resetSiteConfigRemote(); } catch { /* */ } }
    setData({ ...defaultSiteData });
    saveSiteData(defaultSiteData);
  };

  // NEVER render the app with default/mock branding. Until we have either a
  // per-domain cache of the REAL config or a fresh backend response, show a
  // neutral boot loader — prevents the "GameX Hub" default flash on refresh.
  if (!data) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center" style={{ background: '#0a0a0a' }} data-testid="site-boot-loader">
        <div className="relative w-16 h-16">
          <div className="absolute inset-0 rounded-full border-4 border-zinc-800" />
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#d4af37] animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <SiteContext.Provider value={{ data, tenant, update, updateSection, reset, loading, walletUserEnabled, walletAdminEnabled, depositEnabled, refresh }}>
      {children}
    </SiteContext.Provider>
  );
}

export function useSite() {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error('useSite must be used within SiteProvider');
  return ctx;
}
